import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {findNodeHandle, Platform, ScrollView, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {Route, useNavigation} from '@react-navigation/native';
import Screen from '../../shared/components/screen';
import {BreadcrumbItem} from '../../shared/components/breadcrumbs/model';
import {Routes, Screens} from '../../shared/routing';
import {CustomScrollView} from '../../shared/components/scrollView';
import {LoadableContent} from '../../shared/components/loadableContent';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Tabs, TabsProps} from '../../shared/components/tabs';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {useScrollViewMap} from '../../shared/components/yaMap/mobile/useScrollViewMap';
import {getCertificateInfoById, getSellers} from '../../redux/actions/eCertificates';
import {certificateInfoSelector} from '../../redux/selectors/eCertificates';
import {InfoTab, InfoTabMobile, HistoryTab, HistoryTabMobile} from './components';
import {useCertificateItemStyles} from './CertificateItem.style';

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: 'Возможности карты',
    routeName: Screens.CardServices,
    webRoute: Routes.cardService,
  },
  {
    title: 'Электронные сертификаты',
    routeName: Screens.ECertificates,
    webRoute: Routes.eCertificates,
  },
];

export function CertificateItemScreen({route}: {route: Route<string, {id: string}>}) {
  const id = route.params?.id;
  const styles = useCertificateItemStyles();
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const scrollViewRef = useRef<ScrollView>(null);

  const scrollToElement = useCallback(
    (targetElementRef: React.RefObject<View>) => {
      if (Platform.OS === 'web') {
        const targetNode = targetElementRef.current as unknown as HTMLElement;
        targetNode.scrollIntoView({behavior: 'smooth', block: 'center'});
      } else {
        if (scrollViewRef.current == null || targetElementRef.current == null) return;

        const scrollViewNode = findNodeHandle(scrollViewRef.current);

        if (scrollViewNode == null) return;

        targetElementRef.current.measureLayout(
          scrollViewNode,
          (x, y) => scrollViewRef.current?.scrollTo({x, y, animated: true}),
          () => console.log('Failed to measure element'),
        );
      }
    },
    [scrollViewRef, scrollViewRef.current],
  );

  const [activeTruIds, setActiveTruIds] = useState<number[]>();

  const {scrollViewProps, mapProps} = useScrollViewMap();

  const certificateInfo = useSelector(certificateInfoSelector());

  const handleTruClick = useCallback(
    (truId: number, targetElementRef) => {
      // setActiveTruIds([truId]);
      // dispatch(getSellers(truId.toString()));
      // scrollToElement(targetElementRef);
    },
    [setActiveTruIds, dispatch],
  );

  const routes = useMemo(() => {
    if (certificateInfo) {
      return [
        ...breadcrumbs,
        {title: certificateInfo.templateName, routeName: Screens.CertificateItem, webRoute: Routes.certificateItem},
      ];
    }

    return breadcrumbs;
  }, [certificateInfo, id]);

  const tabsItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'info',
        label: 'Сведения',
        isEmpty: !certificateInfo,
        children: isDesktop ? (
          <InfoTab
            certificate={certificateInfo}
            activeTruIds={activeTruIds}
            setActiveTruIds={setActiveTruIds}
            onTruClick={handleTruClick}
          />
        ) : (
          <InfoTabMobile
            mapProps={mapProps}
            certificate={certificateInfo}
            activeTruIds={activeTruIds}
            setActiveTruIds={setActiveTruIds}
            onTruClick={handleTruClick}
          />
        ),
      },
      {
        key: 'history',
        label: 'История',
        isEmpty: !(certificateInfo?.transactions && certificateInfo.transactions.length),
        children: isDesktop ? (
          <HistoryTab
            transactions={certificateInfo?.transactions}
            activeTruIds={activeTruIds}
            setActiveTruIds={setActiveTruIds}
            onTruClick={handleTruClick}
          />
        ) : (
          <HistoryTabMobile
            mapProps={mapProps}
            transactions={certificateInfo?.transactions}
            activeTruIds={activeTruIds}
            setActiveTruIds={setActiveTruIds}
            onTruClick={handleTruClick}
          />
        ),
      },
    ],
    [certificateInfo, activeTruIds, setActiveTruIds, mapProps],
  );

  useEffect(() => {
    if (id) {
      dispatch(getCertificateInfoById(id));
    }
  }, [id]);

  useEffect(() => {
    dispatch(getSellers());
  }, []);

  useEffect(() => {
    if (certificateInfo?.templateName) {
      navigation.setOptions({title: certificateInfo.templateName});
    }
  }, [certificateInfo]);

  return (
    <Screen>
      <CustomScrollView
        ref={scrollViewRef}
        contentInsetAdjustmentBehavior='automatic'
        onRefresh={() => {
          if (id) {
            dispatch(getCertificateInfoById(id));
          }
        }}
        {...scrollViewProps}>
        <LoadableContent loading={!certificateInfo}>
          <View style={styles.sectionContainer}>
            <Breadcrumbs routes={routes} />

            <View style={styles.tabsContainer}>
              <Tabs items={tabsItems} />
            </View>
          </View>
        </LoadableContent>
      </CustomScrollView>
    </Screen>
  );
}
